import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";

const INVOICES_COLLECTION = "invoices";
const PAYMENTS_COLLECTION = "payment-link";

export const FirebaseService = {
  /**
   * Create a new invoice in Firestore
   * @param {Object} invoiceData - Data for the invoice
   * @returns {Promise<string>} - The ID of the created invoice
   */
  createInvoice: async (invoiceData) => {
    try {
      const docRef = await addDoc(collection(db, INVOICES_COLLECTION), {
        ...invoiceData,
        status: "pending",
        createdAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error("Error creating invoice: ", error);
      throw error;
    }
  },

  /**
   * Get an invoice by ID
   * @param {string} id - The invoice ID
   * @returns {Promise<Object>} - The invoice data
   */
  getInvoice: async (id) => {
    try {
      const docSnap = await getDoc(doc(db, INVOICES_COLLECTION, id));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error("No such invoice!");
      }
    } catch (error) {
      console.error("Error getting invoice: ", error);
      throw error;
    }
  },

  /**
   * Track changes to an invoice in real-time
   * @param {string} id - The invoice ID
   * @param {Function} callback - Function to call with updated data
   * @returns {Function} - Unsubscribe function
   */
  trackInvoice: (id, callback) => {
    return onSnapshot(doc(db, INVOICES_COLLECTION, id), (docSnap) => {
      if (docSnap.exists()) {
        callback({ id: docSnap.id, ...docSnap.data() });
      }
    }, (error) => {
      console.error("Error tracking invoice status:", error);
    });
  },

  /**
   * Submit payment information for an invoice
   * @param {string} invoiceId - The invoice ID
   * @param {Object} paymentData - Data for the payment
   * @returns {Promise<void>}
   */
  submitPayment: async (invoiceId, paymentData) => {
    try {
      // 1. Add payment record
      await addDoc(collection(db, PAYMENTS_COLLECTION), {
        invoiceId,
        ...paymentData,
        createdAt: serverTimestamp(),
      });

      // 2. Update invoice status
      const invoiceRef = doc(db, INVOICES_COLLECTION, invoiceId);
      await updateDoc(invoiceRef, {
        status: "paid",
        paidAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error submitting payment: ", error);
      throw error;
    }
  },
};
